import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { resetPasswordRequest, resetResetPasswordState } from '../../redux/slices/authSlice';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import snapmenuLogo from '../../assets/snapmenu.png';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isMountedRef = useRef(true);
  
  const { resetPassword } = useAppSelector(state => state.auth);
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Cleanup effect - reset state when component unmounts
  useEffect(() => {
    isMountedRef.current = true;
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      dispatch(resetResetPasswordState());
    };
  }, [dispatch]);

  // Navigate to login after successful password reset
  useEffect(() => {
    if (resetPassword?.success && isMountedRef.current) {
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          navigate('/login', { replace: true });
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [resetPassword?.success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!id) {
      setValidationError('Invalid or missing user ID');
      return;
    }

    if (passwords.newPassword.length < 6) {
      setValidationError('Password must be at least 6 characters');
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    dispatch(resetPasswordRequest({ 
      id, 
      password: passwords.newPassword 
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Reset Password Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Logo/Header Section */}
          <div className="px-8 py-8 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src={snapmenuLogo} 
                alt="SnapMenuAI Logo" 
                className="h-16 object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create New Password</h2>
          </div>

          {/* Form Section */}
          <div className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* New Password */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <IoMdEyeOff size={20} /> : <IoMdEye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <IoMdEyeOff size={20} /> : <IoMdEye size={20} />}
                  </button>
                </div>
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{validationError}</p>
                </div>
              )}

              {/* API Error */}
              {resetPassword?.error && (
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-red-700 text-sm">{resetPassword.error}</p>
                </div>
              )}

              {/* Success Message */}
              {resetPassword?.success && resetPassword?.message && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-green-700 text-sm">{resetPassword.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={resetPassword?.loading || !passwords.newPassword || !passwords.confirmPassword}
                className="w-full bg-red-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resetPassword?.loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
